import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import BaseInput from "@reusables/BaseInput";
import BaseInputsGrid from "@reusables/BaseInputsGrid";

import { ReactComponent as TrashSVG } from "@assets/icons/ic_trash.svg";
import BaseButton from "@reusables/BaseButton";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import BasePhoneInput from "@reusables/BasePhoneInput";
import BaseMaterialIconButton from "@reusables/BaseMaterialIconButton";
import { ReactComponent as PlusSVG } from "@assets/icons/ic_plus.svg";
import { SupplierFormTyping } from "./TabsController";
import { TabProperties } from "../../types";
import FormErrorWarning from "../modals/FormErrorWarning";
import BaseCheckbox from "@reusables/BaseCheckbox";
import { Stack } from "@mui/material";

export default function GeneralTab({onNext}: TabProperties) {
    const {t} = useTranslation("", {keyPrefix: "suppliers.general.tabs.general"});

    const {control, setValue, getValues} = useFormContext<SupplierFormTyping>();

    const {fields: contacts, append, remove} = useFieldArray({
        control,
        name: "contacts",
        keyName: "loop_id"
    });

    useEffect(() => {
        if (getValues("contacts").length === 0) {
            append({});
        }
    }, [contacts]);

    return(
        <form className="space-y-[32px]" onSubmit={onNext}>
            <FormErrorWarning />
            
            <BaseInputsGrid>
                <Controller
                    control={control}
                    name="name"
                    render={({field, fieldState}) => (
                        <BaseInput
                            {...field}
                            error={fieldState.error}

                            label={t("fields.supplierName.label") + " *"}
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="code"
                    render={({field, fieldState}) => (
                        <BaseInput
                            {...field}
                            error={fieldState.error}

                            label={t("fields.supplierCode.label")}
                            
                            disabled={getValues("mode") === "editing"}
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="vat"
                    render={({field, fieldState}) => (
                        <BaseInput
                            {...field}
                            error={fieldState.error}

                            label={t("fields.registrationNumber.label")}
                        />
                    )}
                />
            </BaseInputsGrid>

            <div>
                <div className="flex items-center justify-between">
                    <div className="text-xl text-accent">{t("categories.contactInfo")}</div>
                    <BaseMaterialIconButton
                        icon={<PlusSVG />}
                        onClick={() => append({})}
                    />
                </div>

                {contacts.map((contact, index) => {
                    const isExtraContact = index > 0;
                    const isLastContact = index === contacts.length - 1;

                    return (
                        <div
                            key={contact.loop_id}
                            style={getReferenceItemStyle(isLastContact)}
                        >
                            <div className="grid grid-cols-1 gap-y-[24px] lg:grid-cols-3 lg:gap-x-[40px]">
                                <div className="min-w-0">
                                    <Controller
                                        control={control}
                                        name={`contacts.${index}.name`}
                                        render={({field, fieldState}) => (
                                            <BaseInput
                                                {...field}
                                                error={fieldState.error}
                                                placeholder={t("fields.contactName.label")}
                                            />
                                        )}
                                    />
                                </div>

                                <div className="min-w-0">
                                    <Controller
                                        control={control}
                                        name={`contacts.${index}.phone`}
                                        render={({field, fieldState}) => (
                                            <BasePhoneInput
                                                {...field}
                                                {...fieldState}
                                                placeholder={t('fields.phone.label')}
                                                size='md'
                                                variant='custom'
                                            />
                                        )}
                                    />
                                </div>

                                <div className="min-w-0">
                                    <Controller
                                        control={control}
                                        name={`contacts.${index}.email`}
                                        render={({field, fieldState}) => (
                                            <BaseInput
                                                {...field}
                                                error={fieldState.error}
                                                placeholder={t("fields.email.label")}
                                                type={"email"}
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            {isExtraContact && (
                                <div className="flex justify-end mt-[24px]">
                                    <div
                                        className="text-gray-300 space-x-[10px] font-thin cursor-pointer hover:text-actionableIcons"
                                        onClick={() => {
                                            if(contact.id != undefined) {
                                                setValue("deleted_contacts", [...getValues("deleted_contacts"), contact.id]);
                                            }
                                            remove(index);
                                        }}
                                    >
                                        <TrashSVG />
                                        <span className="align-middle">{t("buttons.delete")}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div>
                <div className="text-xl text-accent mb-[16px]">{t("categories.roles")}</div>
                <Stack
                    direction={"row"}
                    spacing={2}
                >
                    <BaseCheckbox
                        value
                        disabled
                        label={t("checkboxes.isSupplier")}
                    />

                    <Controller
                        control={control}
                        name="is_customer"
                        render={({field}) => (
                            <BaseCheckbox
                                value={field.value}
                                onChange={field.onChange}
                                label={t("checkboxes.isCustomer")}
                            />
                        )}
                    />
                </Stack>
            </div>
            <div className="flex justify-center">
                <BaseButton
                    text={t("buttons.next")}
                    size="md"
                    buttonWidth="200px"
                />
            </div>
            {/*<DevTool control={control} />*/}
        </form>
    );
}

const getReferenceItemStyle = (isLastItem: boolean): React.CSSProperties => ({
    padding: "24px 0",
    ...(isLastItem ? {} : { borderBottom: "1px solid #D9D6DA" }),
});
